import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import "./Modal.css";

type ModalProps = {
  children: ReactNode;
  className: string;
  dataTest: string;
  dismissalDisabled?: boolean;
  isOpen: boolean;
  labelledBy: string;
  onClose: () => void;
  overlayClassName: string;
};

const FOCUSABLE_SELECTOR =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function Modal({
  children,
  className,
  dataTest,
  dismissalDisabled = false,
  isOpen,
  labelledBy,
  onClose,
  overlayClassName,
}: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dismissalRef = useRef({ dismissalDisabled, onClose });

  // Match the committed button state before an Escape event can arrive.
  useLayoutEffect(() => {
    dismissalRef.current = { dismissalDisabled, onClose };
  }, [dismissalDisabled, onClose]);

  useEffect(() => {
    const panel = panelRef.current;
    const overlay = overlayRef.current;
    if (!isOpen || !panel || !overlay) return;

    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const backgroundElements = Array.from(document.body.children)
      .filter(
        (element): element is HTMLElement =>
          element instanceof HTMLElement && element !== overlay,
      )
      .map((element) => ({ element, wasInert: element.inert }));

    const getFocusableElements = () =>
      Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter(
        (element) =>
          element.tabIndex >= 0 &&
          !element.matches(":disabled") &&
          !element.closest("[inert]") &&
          element.getClientRects().length > 0 &&
          getComputedStyle(element).visibility !== "hidden",
      );
    const focusFirst = () => {
      (getFocusableElements()[0] ?? panel).focus({ preventScroll: true });
    };
    const handleFocus = (event: FocusEvent) => {
      if (event.target instanceof Node && !panel.contains(event.target)) {
        focusFirst();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (!dismissalRef.current.dismissalDisabled) {
          dismissalRef.current.onClose();
        }
      } else if (event.key === "Tab") {
        const focusableElements = getFocusableElements();
        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];
        if (!first || !last) {
          event.preventDefault();
          panel.focus();
        } else if (
          !panel.contains(document.activeElement) ||
          document.activeElement === panel ||
          (event.shiftKey && document.activeElement === first) ||
          (!event.shiftKey && document.activeElement === last)
        ) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        }
      }
    };

    document.body.style.overflow = "hidden";
    backgroundElements.forEach(({ element }) => {
      element.inert = true;
    });
    focusFirst();
    document.addEventListener("focusin", handleFocus);
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("focusin", handleFocus);
      document.removeEventListener("keydown", handleKeyDown, true);
      document.body.style.overflow = previousOverflow;
      backgroundElements.forEach(({ element, wasInert }) => {
        element.inert = wasInert;
      });
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) {
        previousFocus.focus({ preventScroll: true });
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Keep visibility, stacking and the backdrop in the DOM for session replay.
  return createPortal(
    <div
      className={`react-pdp__modal-overlay ${overlayClassName}`}
      data-test={`${dataTest}-backdrop`}
      onClick={(event) => {
        if (event.target === event.currentTarget && !dismissalDisabled)
          onClose();
      }}
      ref={overlayRef}
    >
      <div
        aria-labelledby={labelledBy}
        aria-modal="true"
        className={className}
        data-test={dataTest}
        ref={panelRef}
        role="dialog"
        tabIndex={-1}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
